import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CartSidebar from "@/components/feature/CartSidebar";
import ProductCard from "@/components/base/ProductCard";
import { useProducts } from "@/hooks/useProducts";

const SORT_OPTIONS = [
  { label: "Featured", value: "featured" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
  { label: "Newest First", value: "newest" },
  { label: "Best Rated", value: "rating" },
];

const MATERIAL_TYPES = [
  "Luminous Satin",
  "Holographic",
  "Mirror Metallic",
  "Structured Satin",
  "Crushed Velvet",
  "Iridescent Sequin",
];

const BAG_STYLES = [
  "Clutch",
  "Shoulder Bag",
  "Tote",
  "Mini Bag",
  "Bucket Bag",
  "Crossbody",
];

export default function BagsPage() {
  const [sortBy, setSortBy] = useState("featured");
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [priceMax, setPriceMax] = useState(150);
  const [filterOpen, setFilterOpen] = useState(false);

  const toggleMaterial = (m: string) =>
    setSelectedMaterials((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
    );

  const toggleStyle = (s: string) =>
    setSelectedStyles((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );

  const { products: bagsProducts, loading } = useProducts({ category: "bags" });

  const filtered = useMemo(() => {
    let list = [...bagsProducts];
    if (selectedMaterials.length > 0)
      list = list.filter((p) => selectedMaterials.includes(p.fabricLabel));
    list = list.filter((p) => p.price <= priceMax);
    switch (sortBy) {
      case "price_asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price_desc":
        list.sort((a, b) => b.price - a.price);
        break;
      case "newest":
        list.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      case "rating":
        list.sort((a, b) => b.rating - a.rating);
        break;
      default:
        list.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
    }
    return list;
  }, [bagsProducts, selectedMaterials, selectedStyles, priceMax, sortBy]);

  const activeFilterCount =
    selectedMaterials.length + selectedStyles.length + (priceMax < 150 ? 1 : 0);

  return (
    <div className="min-h-screen bg-moonlight" style={{ fontFamily: "'Jost', sans-serif" }}>
      <Navbar />
      <CartSidebar />

      {/* Page Hero */}
      <section className="relative pt-20 lg:pt-24 overflow-hidden">
        <div className="relative h-56 md:h-72">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20editorial%20banner%20of%20multiple%20glamorous%20handbags%20arranged%20artfully%20including%20satin%20clutch%20holographic%20shoulder%20bag%20gold%20metallic%20mini%20bag%20and%20velvet%20bucket%20bag%2C%20dark%20atmospheric%20background%20with%20dramatic%20individual%20spotlights%2C%20high%20fashion%20accessory%20campaign%20photography%2C%20silver%20gold%20tones%2C%20moonlight%20aesthetic&width=1440&height=400&seq=iif_bags_banner_001&orientation=landscape"
            alt="i-bag collection banner"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-midnight/75 via-midnight/45 to-midnight/20" />
          <div className="relative h-full flex flex-col justify-center px-8 lg:px-16">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-moonlight/50 text-[10px] tracking-wider uppercase mb-3">
              <Link
                to="/"
                className="hover:text-moonlight transition-colors cursor-pointer"
              >
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-moonlight/80">i-bag</span>
            </div>
            <h1
              className="text-4xl md:text-5xl font-light italic text-moonlight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              i-bag
            </h1>
            <p className="text-xs tracking-widest uppercase text-moonlight/60 mt-1">
              Luminous Accessories
            </p>
          </div>
        </div>
      </section>

      {/* Category Quick Nav */}
      <div className="border-b border-pearl/40 bg-moonlight">
        <div className="max-w-7xl mx-auto px-5 lg:px-16 py-4 flex items-center gap-6 overflow-x-auto">
          <span className="text-[10px] tracking-widest uppercase text-midnight/40 shrink-0">
            Shop by:
          </span>
          {["All Bags", "Clutches", "Shoulder Bags", "Tote Bags", "Mini Bags", "Bucket Bags"].map(
            (label) => (
              <button
                key={label}
                className="text-xs text-midnight/60 hover:text-midnight transition-colors whitespace-nowrap cursor-pointer pb-0.5 hover:border-b hover:border-midnight/40"
              >
                {label}
              </button>
            )
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-5 lg:px-16 py-10">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-7 pb-5 border-b border-pearl/40">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setFilterOpen((v) => !v)}
              className={`flex items-center gap-2 text-xs tracking-wider uppercase border px-4 py-2.5 transition-all cursor-pointer whitespace-nowrap ${
                filterOpen || activeFilterCount > 0
                  ? "border-midnight bg-midnight text-moonlight"
                  : "border-pearl text-midnight/60 hover:border-midnight hover:text-midnight"
              }`}
            >
              <i className="ri-filter-line" />
              Filter
              {activeFilterCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-moon-gold-dark text-midnight text-[10px] flex items-center justify-center leading-none">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  setSelectedMaterials([]);
                  setSelectedStyles([]);
                  setPriceMax(150);
                }}
                className="text-[10px] tracking-wider uppercase text-midnight/40 hover:text-midnight transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-midnight/40">
              {filtered.length} results
            </span>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs tracking-wide border border-pearl bg-transparent text-midnight px-3 py-2.5 pr-7 appearance-none cursor-pointer focus:outline-none focus:border-midnight"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-midnight/40 pointer-events-none text-sm" />
            </div>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Filter Sidebar */}
          {filterOpen && (
            <aside className="hidden lg:block w-56 shrink-0">
              {/* Material */}
              <div className="mb-8">
                <h3 className="text-[10px] tracking-widest uppercase text-midnight/50 mb-4">
                  Material
                </h3>
                <div className="space-y-2.5">
                  {MATERIAL_TYPES.map((m) => (
                    <label
                      key={m}
                      className="flex items-center gap-2.5 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMaterials.includes(m)}
                        onChange={() => toggleMaterial(m)}
                        className="w-3.5 h-3.5 border-pearl accent-midnight cursor-pointer"
                      />
                      <span className="text-xs text-midnight/60 group-hover:text-midnight transition-colors">
                        {m}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Bag Style */}
              <div className="mb-8">
                <h3 className="text-[10px] tracking-widest uppercase text-midnight/50 mb-4">
                  Style
                </h3>
                <div className="space-y-2.5">
                  {BAG_STYLES.map((s) => (
                    <label
                      key={s}
                      className="flex items-center gap-2.5 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedStyles.includes(s)}
                        onChange={() => toggleStyle(s)}
                        className="w-3.5 h-3.5 border-pearl accent-midnight cursor-pointer"
                      />
                      <span className="text-xs text-midnight/60 group-hover:text-midnight transition-colors">
                        {s}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div>
                <h3 className="text-[10px] tracking-widest uppercase text-midnight/50 mb-4">
                  Max Price:{" "}
                  <span className="text-midnight">${priceMax}</span>
                </h3>
                <input
                  type="range"
                  min={20}
                  max={150}
                  step={5}
                  value={priceMax}
                  onChange={(e) => setPriceMax(Number(e.target.value))}
                  className="w-full accent-midnight cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-midnight/30 mt-1">
                  <span>$20</span>
                  <span>$150</span>
                </div>
              </div>
            </aside>
          )}

          {/* Product Grid */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5 lg:gap-7">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-pearl/30 aspect-[3/4] mb-3" />
                    <div className="bg-pearl/30 h-4 w-3/4 mb-2" />
                    <div className="bg-pearl/30 h-3 w-1/2" />
                  </div>
                ))}
              </div>
            ) : filtered.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-5 lg:gap-7">
                {filtered.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-24">
                <i className="ri-handbag-line text-3xl text-midnight/20 mb-4 block" />
                <p className="text-sm text-midnight/40">No bags match your filters.</p>
                <button
                  onClick={() => {
                    setSelectedMaterials([]);
                    setSelectedStyles([]);
                    setPriceMax(150);
                  }}
                  className="mt-4 text-xs tracking-widest uppercase text-midnight border-b border-midnight/30 pb-0.5 hover:border-midnight transition-colors cursor-pointer whitespace-nowrap"
                >
                  Clear Filters
                </button>
              </div>
            )}

            {/* Styling Tips Banner */}
            <div className="mt-16 bg-midnight text-moonlight px-8 py-10 flex flex-col md:flex-row items-center gap-6">
              <div className="w-12 h-12 flex items-center justify-center shrink-0 border border-pearl/20 rounded-full">
                <i className="ri-sparkling-line text-moon-gold text-xl" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <p
                  className="text-xl font-light italic mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Complete your glow.
                </p>
                <p className="text-xs text-moonlight/50 leading-relaxed">
                  Every i-bag is designed to complement our clothing and shoes. Mix textures —
                  pair a velvet bucket with a satin dress, or let a metallic mini bag anchor an
                  all-black look.
                </p>
              </div>
              <div className="flex gap-3 shrink-0">
                <Link
                  to="/collections/clothes"
                  className="px-5 py-2.5 border border-pearl/30 text-moonlight text-[10px] tracking-widest uppercase hover:bg-moonlight/10 hover:border-pearl/60 transition-all cursor-pointer whitespace-nowrap"
                >
                  i-clothes
                </Link>
                <Link
                  to="/collections/shoes"
                  className="px-5 py-2.5 border border-pearl/30 text-moonlight text-[10px] tracking-widest uppercase hover:bg-moonlight/10 hover:border-pearl/60 transition-all cursor-pointer whitespace-nowrap"
                >
                  i-shoes
                </Link>
              </div>
            </div>

            {/* Shipping Info Strip */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-pearl/30">
              {[
                {
                  icon: "ri-truck-line",
                  title: "Free US Shipping",
                  desc: "On orders over $80",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "30-Day Returns",
                  desc: "Hassle-free exchanges",
                },
                {
                  icon: "ri-lock-2-line",
                  title: "Secure Checkout",
                  desc: "PayPal & Credit Card",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-moonlight-warm px-6 py-5 flex items-center gap-4"
                >
                  <div className="w-9 h-9 flex items-center justify-center shrink-0">
                    <i className={`${item.icon} text-lg text-midnight/40`} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-midnight">{item.title}</p>
                    <p className="text-[10px] text-midnight/40 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
